import { Badge, IconButtonWithTooltip, Text } from '@elkaso-app/web-design';
import { mdiTrashCanOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
  PriceImportRun,
  priceImportsEndpointIdentifier,
  useDeletePriceImportRunApi,
  useGetPriceImportRunsApi,
} from 'apis/price-imports';
import { BaseTableContainer, ExcelTable } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, TableCell, TableHeader } from 'components/base-table/components';
import { StartImportModal } from 'pages/price-imports/components/start-import-modal';
import { runStatusPresentation } from 'pages/price-imports/utils/run-status';
import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

interface IColumnsAccessorData {
  id: number;
  file: PriceImportRun;
  supplierId: number;
  status: PriceImportRun;
  rows: PriceImportRun;
  createdAt: string;
  actions: PriceImportRun;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export const PriceImportRunsScreen = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data } = useGetPriceImportRunsApi();

  const { mutateAsync: deleteRun } = useDeletePriceImportRunApi({
    onSuccess: () => {
      queryClient.invalidateQueries([priceImportsEndpointIdentifier]);
    },
  });

  const confirmDelete = (run: PriceImportRun) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the import of "${run.originalFileName}"? Its rows and decisions are discarded.`)) {
      return;
    }

    deleteRun({ runId: run.id }).catch(() => undefined);
  };

  const runs = data?.data;

  useEffect(() => {
    dispatch(changeTitle('Price Imports'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='File' />,
        accessor: 'file',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['file']>) => (
          <div>
            <Link to={`/app/price-imports/${value.id}`}>
              <Text fontWeight='semibold' color='red.500' noOfLines={1}>
                {value.originalFileName}
              </Text>
            </Link>
            <Text fontSize='xs' color='gray.500'>
              {value.sheetName ? `${value.sheetName} · ` : ''}
              {value.sourceKind.toUpperCase()}
            </Text>
          </div>
        ),
      },
      {
        Header: <TableHeader name='Supplier' />,
        accessor: 'supplierId',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplierId']>) => <TableCell value={`#${value}`} />,
      },
      {
        Header: <TableHeader name='Status' />,
        accessor: 'status',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['status']>) => {
          const presentation = runStatusPresentation(value.status);

          return (
            <div>
              <Badge colorScheme={presentation.colorScheme} variant='subtle'>
                {presentation.label}
              </Badge>
              {value.lastError && (
                <Text fontSize='xs' color='gray.500' noOfLines={1} title={value.lastError}>
                  {value.lastError}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        Header: <TableHeader name='Rows' />,
        accessor: 'rows',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['rows']>) => (
          <div>
            <Text whiteSpace='nowrap'>{value.totalRows} total</Text>
            <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
              {value.committedRows > 0 ? `${value.committedRows} committed · ` : ''}
              {value.reviewRows} to review
            </Text>
          </div>
        ),
      },
      {
        Header: <TableHeader name='Uploaded' />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <TableCell value={formatDate(value)} whiteSpace='nowrap' />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => {
          // Committed runs are the audit trail for the prices they wrote; the
          // BE refuses them too, but a disabled control explains itself.
          const isAuditTrail = value.status === 'committed' || value.status === 'partially_committed';

          return (
            <IconButtonWithTooltip
              size='sm'
              variant='unstyled'
              label={isAuditTrail ? 'Kept as the audit trail for the prices it wrote' : 'Remove this import'}
              aria-label={`delete-import-${value.id}`}
              Icon={<Icon path={mdiTrashCanOutline} size={0.8} />}
              isDisabled={isAuditTrail || value.status === 'committing'}
              onClick={() => confirmDelete(value)}
            />
          );
        },
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      (runs ?? []).map((run) => ({
        // BaseTable needs a defined unique id per row, or every row renders blank.
        id: run.id,
        file: run,
        supplierId: run.supplierId,
        status: run,
        rows: run,
        createdAt: run.createdAt,
        actions: run,
      })),
    [runs]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Price Imports' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <StartImportModal />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <ExcelTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>
    </BaseTableContainer>
  );
};
